import { Controller } from "@hotwired/stimulus"

const DATE_PLACEHOLDER = "__DATE__"

export default class extends Controller {
  static targets = ["panel", "input", "submit", "row",
                    "tc1Body", "tc2Body", "tc1Divider", "tc2Divider",
                    "tc1Header", "tc2Header", "tc1Empty", "tc2Empty", "totals"]
  static values = {
    periodStart: String,
    periodEnd: String,
    defaultDate: String,
    dayNames: { type: Array, default: [] },
    tc1Label: String,
    tc2Label: String,
    tc1UntilTemplate: String,
    tc2FromTemplate: String,
    splitting: { type: Boolean, default: false },
  }

  connect() {
    this.boundReapplyAfterMorph = this.reapplyAfterMorph.bind(this)
    document.addEventListener("turbo:morph", this.boundReapplyAfterMorph)
    this.splitting = this.splittingValue
    this.splitDateCache = this.splitDate()
    this.applyClientState()
  }

  disconnect() {
    if (this.boundReapplyAfterMorph) document.removeEventListener("turbo:morph", this.boundReapplyAfterMorph)
  }

  reapplyAfterMorph() {
    this.splittingValue = this.splitting
    if (this.splitting && this.splitDateCache) this.inputTarget.value = this.splitDateCache
    this.applyClientState()
  }

  applyClientState() {
    if (!this.splitMarkupIsRendered) return

    this.orderedRows = [...this.rowTargets]
    this.render()
  }

  get splitMarkupIsRendered() {
    return this.hasTc1EmptyTarget
  }

  toggle() {
    this.splittingValue = !this.splittingValue
    this.splitting = this.splittingValue
    if (this.splittingValue && !this.inputTarget.value) {
      this.inputTarget.value = this.defaultDateValue || this.periodStartValue || ""
    }
    this.splitDateCache = this.splitDate()
    this.render()
  }

  update() {
    this.splitDateCache = this.splitDate()
    this.render()
  }

  stepDay(event) {
    const step = Number(event.params.step || event.currentTarget.dataset.step || 0)
    const current = this.splitDate() || this.defaultDateValue || this.periodStartValue
    if (!current) return

    let next = this.addDays(current, step)
    const min = this.periodStartValue
    const max = this.maxSplitDate()
    if (min && next < min) next = min
    if (max && next > max) next = max
    this.inputTarget.value = next
    this.splitDateCache = next
    this.render()
  }

  render() {
    const active = this.splittingValue
    if (this.hasPanelTarget) this.panelTarget.hidden = !active
    this.tc1DividerTargets.forEach(el => (el.hidden = !active))
    if (this.hasTc2BodyTarget) this.tc2BodyTarget.hidden = !active
    if (this.hasTotalsTarget) this.totalsTarget.hidden = active

    const splitDate = active ? this.splitDate() : null
    this.distribute(splitDate)
    this.refreshHeaders(splitDate)
    this.refreshSubmit(active, splitDate)
  }

  distribute(splitDate) {
    let tc1 = 0
    let tc2 = 0
    this.orderedRows.forEach(row => {
      const onTc1 = !splitDate || this.belongsToFirstCard(row, splitDate)
      const body = onTc1 ? this.tc1BodyTarget : this.tc2BodyTarget
      const anchor = onTc1 ? this.tc1EmptyTarget : this.tc2EmptyTarget
      body.insertBefore(row, anchor)
      onTc1 ? tc1++ : tc2++
    })
    this.tc1EmptyTarget.hidden = tc1 > 0
    if (this.hasTc2EmptyTarget) this.tc2EmptyTarget.hidden = tc2 > 0
  }

  belongsToFirstCard(row, splitDate) {
    const workedOn = row.dataset.workedOn
    if (!workedOn) return true
    return workedOn <= splitDate
  }

  refreshHeaders(splitDate) {
    if (splitDate) {
      const start = this.formatDisplay(splitDate)
      const next = this.formatDisplay(this.addDays(splitDate, 1))
      this.tc1HeaderTargets.forEach(el => (el.textContent = this.tc1UntilTemplateValue.replace(DATE_PLACEHOLDER, start)))
      this.tc2HeaderTargets.forEach(el => (el.textContent = this.tc2FromTemplateValue.replace(DATE_PLACEHOLDER, next)))
    } else {
      this.tc1HeaderTargets.forEach(el => (el.textContent = this.tc1LabelValue))
      this.tc2HeaderTargets.forEach(el => (el.textContent = this.tc2LabelValue))
    }
  }

  refreshSubmit(active, splitDate) {
    if (!this.hasSubmitTarget) return

    const valid = active && Boolean(splitDate) &&
      Boolean(this.periodStartValue) && Boolean(this.periodEndValue) &&
      splitDate >= this.periodStartValue && splitDate < this.periodEndValue
    this.submitTarget.disabled = !valid
  }

  maxSplitDate() {
    return this.periodEndValue ? this.addDays(this.periodEndValue, -1) : null
  }

  splitDate() {
    if (!this.hasInputTarget) return null

    const value = this.inputTarget.value
    return value ? value.slice(0, 10) : null
  }

  formatDisplay(iso) {
    const [y, m, d] = iso.split("-").map(Number)
    const weekday = this.dayNamesValue[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] || ""
    const dd = String(d).padStart(2, "0")
    const mm = String(m).padStart(2, "0")
    return `${weekday} ${dd}-${mm}-${y}`.trim()
  }

  addDays(iso, amount) {
    const [y, m, d] = iso.split("-").map(Number)
    const date = new Date(Date.UTC(y, m - 1, d))
    date.setUTCDate(date.getUTCDate() + amount)
    const yy = date.getUTCFullYear()
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0")
    const dd = String(date.getUTCDate()).padStart(2, "0")
    return `${yy}-${mm}-${dd}`
  }
}
